import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Inicio', href: '#home' },
    { name: 'IEEE', href: '#about-ieee' },
    { name: 'Rama Estudiantil', href: '#student-branch' },
    { name: 'Sobre ESPOCH', href: '#about-espoch' },
    { name: 'Capítulos', href: '#chapters' },
    { name: 'Equipo', href: '#team' },
  ];

  return (
    <motion.nav
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/80 backdrop-blur-lg border-b border-slate-200 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-8 py-4 flex items-center justify-between">
        {/* Logo */}
        <motion.a
          href="#home"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.25em] text-slate-800 transition-colors duration-300 hover:text-cyan-600"
        >
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden transition-all duration-300 hover:border-cyan-200">
            <svg className="h-5 w-5 text-cyan-600" fill="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" opacity="0.1" />
              <circle cx="12" cy="12" r="6" />
            </svg>
          </span>
          <span className="hidden sm:inline">IEEE ESPOCH</span>
        </motion.a>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-1">
          {navItems.map((item, index) => (
            <motion.a
              key={index}
              href={item.href}
              whileHover={{ y: -2 }}
              className="px-4 py-2 text-sm font-medium text-slate-600 transition-colors duration-300 hover:text-cyan-600 rounded-lg hover:bg-slate-50"
            >
              {item.name}
            </motion.a>
          ))}
        </div>

        {/* CTA Button - Desktop */}
        <div className="hidden lg:flex items-center gap-4">
          <motion.a
            href="#cta"
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex items-center justify-center px-6 py-2.5 rounded-lg font-semibold text-sm uppercase tracking-[0.15em] bg-gradient-to-r from-cyan-600 to-cyan-500 text-white transition-all duration-300 shadow-[0_10px_25px_rgba(56,189,248,0.2)] hover:shadow-[0_15px_35px_rgba(56,189,248,0.3)]"
          >
            Únete
          </motion.a>
        </div>

        {/* Mobile menu button */}
        <motion.button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-label="Abrir menú"
          whileTap={{ scale: 0.95 }}
          className="lg:hidden rounded-lg border border-slate-200 bg-white p-2.5 text-slate-800 transition-all duration-300 hover:bg-slate-50 hover:border-slate-300 shadow-sm"
        >
          <div className="space-y-1.5 w-5">
            <motion.span
              animate={isOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }}
              className="block h-0.5 w-full rounded-full bg-slate-800 transition-all duration-300"
            />
            <motion.span
              animate={isOpen ? { opacity: 0 } : { opacity: 1 }}
              className="block h-0.5 w-full rounded-full bg-slate-800 transition-all duration-300"
            />
            <motion.span
              animate={isOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }}
              className="block h-0.5 w-full rounded-full bg-slate-800 transition-all duration-300"
            />
          </div>
        </motion.button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden border-t border-slate-200 bg-white/95 backdrop-blur-lg"
          >
            <div className="space-y-1 px-6 sm:px-8 py-6">
              {navItems.map((item, index) => (
                <motion.a
                  key={index}
                  href={item.href}
                  onClick={() => setIsOpen(false)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="block rounded-lg px-4 py-3 text-sm font-medium text-slate-600 transition-all duration-300 hover:bg-slate-50 hover:text-cyan-600"
                >
                  {item.name}
                </motion.a>
              ))}
              <motion.a
                href="#cta"
                onClick={() => setIsOpen(false)}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: navItems.length * 0.05 }}
                className="block rounded-lg border border-cyan-200 bg-cyan-50 px-4 py-3 text-center text-sm font-semibold text-cyan-700 transition-all duration-300 hover:bg-cyan-100 mt-4"
              >
                Únete
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
